import gql from "graphql-tag";

// eslint-disable-next-line no-restricted-imports -- TODO(FEI-7051): switch to useGql
import type {ApolloGqlClient, ApolloResult} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import Log, {Errors} from "@khan/logging";

import type {
    getContentForStandardType,
    getStandardsDataForLocaleRegionAndDomainType,
} from "./__graphql-types__/index.ts";
import type {Standard} from "./types.ts";

// A GraphQL query to get all sets of standards relevant to the specified
// locale, and, for each of those sets, all standards they define.
// See content/standards/set_of_standards.py for documentation on
// standards and sets of standards.
export const STANDARDS_DATA_QUERY: GqlOperation<
    getStandardsDataForLocaleRegionAndDomainType["response"],
    getStandardsDataForLocaleRegionAndDomainType["variables"]
> = gqlOp(gql`
    query getStandardsDataForLocaleRegionAndDomain(
        $locale: String
        $region: String
        $domain: String
    ) {
        allSetsOfStandards(locale: $locale, region: $region, domain: $domain) {
            id
            isDiscoverable
            name
            shortName
            standards {
                id
                standardId
                description
                children
                relativeUrl
            }
        }
    }
`);

// A GraphQL query to fetch list of [kind]:[content_id] for any content
// that matches this set id and optional standard id.
const STANDARDS_CONTENT_QUERY: GqlOperation<
    getContentForStandardType["response"],
    getContentForStandardType["variables"]
> = gqlOp(gql`
    query getContentForStandard(
        $set: String!
        $region: String
        $standard: String
    ) {
        contentForStandardMappings(
            setId: $set
            region: $region
            standardId: $standard
        )
    }
`);

/**
 * This class uses GraphQL to query hierarchy and description information
 * for all standards relvant to the specified locale, and defines methods
 * that look up information for a specified standards. This is useful when
 * you've queried the standards associated with a content item using
 * mappedStandardIds and you've got an array of "setId|standardId" strings.
 *
 * Products like the coach dashboard that have to display lots of standards
 * can use this class to query all of the hierarchy and description data up
 * front and then use really efficient queries that return standard mappings
 * as an array of "setId|standardId" string.
 */
export default class StandardsData {
    sets: any;

    // Make a GraphQL query to retreive standards data for sets of standards
    // relevant to the specified locale and (optional) domain.
    static fetch(
        gqlClient: ApolloGqlClient,
        locale: string | null,
        region: string | null,
        domain: string | null,
    ): Promise<StandardsData> {
        return gqlClient
            .query(STANDARDS_DATA_QUERY, {
                variables: {locale, region, domain},
                fetchPolicy: "cache-first",
            })
            .then((result) => new StandardsData(result));
    }

    // Make a GraphQL query to retrieve content ids that are mapped to the
    // specified set and (optional) standard.
    static fetchContent(
        gqlClient: ApolloGqlClient,
        set: string,
        standard: string | null,
        region: string | null,
        cached: boolean | null = false,
    ): Promise<ApolloResult<getContentForStandardType["response"]>> {
        return gqlClient.query(STANDARDS_CONTENT_QUERY, {
            variables: {region, set, standard},
            fetchPolicy: cached ? "cache-first" : "network-only",
        });
    }

    // This constructor isn't intended to be called directly. Instead, use
    // the fetch() factory method which will run a GraphQL query and invoke
    // this constructor asynchronously when the query results arrive
    constructor(
        result: ApolloResult<
            getStandardsDataForLocaleRegionAndDomainType["response"]
        >,
    ) {
        // The query results is an array of set objects, each of which
        // includes an array of standard objects. Since we need to look
        // up sets and standards by ids, we want to convert these to objects
        // that map from ids to sets and ids to standards.
        const sets: Record<string, any> = {};

        for (const set of result.data?.allSetsOfStandards ?? []) {
            if (!set || !set.id) {
                continue;
            }
            const setId = set.id;
            sets[setId] = {
                isDiscoverable: set.isDiscoverable,
                name: set.name,
                shortName: set.shortName,
                standards: {},
            };
            // Standards have a defined sequence within their set.
            // We assume that the graphql query preserves this sequence,
            // and we use the index property below so that we can sort
            // standards into their proper order.
            if (set.standards) {
                set.standards.forEach((standard, index) => {
                    const {children, description, relativeUrl, standardId} =
                        standard || {};
                    if (standardId) {
                        sets[setId].standards[standardId] = {
                            description,
                            children,
                            index: index,
                            relativeUrl,
                        };
                    }
                });
            }
        }

        // Each standard in the results has an optional array of children.
        // We need to be able to go in the other direction from a standard
        // to its parent standard, so loop through the data again to add
        // a parent property for each standard that has a parent.
        for (const setId of Object.keys(sets)) {
            const set = sets[setId];
            for (const standardId of Object.keys(set.standards)) {
                const standard = set.standards[standardId];
                for (const childId of standard.children || []) {
                    const child = set.standards[childId];
                    if (!child) {
                        // All childIds should be valid standards that exist
                        // as part of the set of standard, but if it doesn't
                        // we should log this error and continue loading the
                        // remaining standards
                        Log.error(
                            `Child standard ${childId} does not exist within set ${setId}`,
                            Errors.InvalidInput,
                        );
                        continue;
                    }

                    if (child.parent) {
                        // This should not happen if our standards are
                        // defined correctly. But log to if it does happen.
                        Log.error(
                            "Standard has too many parents",
                            Errors.InvalidInput,
                            {
                                sentryData: {
                                    contexts: {
                                        extras: {
                                            setId,
                                            childId,
                                            parentId: child.parent.id,
                                            standardId,
                                        },
                                    },
                                },
                            },
                        );
                    }
                    child.parent = standardId;
                }
            }
        }

        // This sets object we've constructed is the state that all the
        // methods below use.
        this.sets = sets;
    }

    /**
     * Given a standard in setId|standardId form, returns isDiscoverable value
     * of the standard set. Throws an error if the standards data has not yet
     * been fetched from the server. Returns false if the setId is unknown.
     *
     * For example getSetName("CCSS.Math|8.G.2.A") might return true
     */
    getSetIsDiscoverable(standard: string): boolean {
        const [setId] = standard.split("|");
        const set = this.sets[setId];

        if (set) {
            return set.isDiscoverable;
        }
        // If we don't find the set, then our standards data is out
        // of sync with our mappings data, and that is bad enough to
        // log
        Log.error("Standard set not found", Errors.InvalidInput, {
            sentryData: {
                contexts: {
                    extras: {
                        setId,
                    },
                },
            },
        });
        return false;
    }

    /**
     * Given a standard in setId|standardId form, returns the human-readable
     * name of the standard set. Throws an error if the standards data
     * has not yet been fetched from the server. Returns null if the
     * setId is unknown. If the set of standards has a short name, we
     * return that. Otherwise we return the full name.
     *
     * For example getSetName("CCSS.Math|8.G.2.A") might return
     * "Common Core Math"
     */
    getSetName(standard: string): string | null | undefined {
        const [setId] = standard.split("|");
        const set = this.sets[setId];

        if (set) {
            return set.shortName || set.name;
        }
        // If we don't find the set, then our standards data is out
        // of sync with our mappings data, and that is bad enough to
        // log
        Log.error("Standard set not found", Errors.InvalidInput, {
            sentryData: {
                contexts: {
                    extras: {
                        setId,
                    },
                },
            },
        });
        return null;
    }

    /**
     * Given a standard in setId|standardId form, returns the human-readable
     * description of the standard. Throws an error if the standards data
     * has not yet been fetched from the server. Returns null if the setId
     * or standardId is not known.
     *
     * For example getStandardDescription("CCSS.Math|8.G.2.A") might return
     * "Understand the Pythagorean theorem"
     */
    getStandardDescription(standard: string): string | null | undefined {
        const [setId, standardId] = standard.split("|");
        const set = this.sets[setId];
        if (set) {
            const standard = set.standards[standardId];
            if (standard) {
                return standard.description;
            }
        }
        Log.error("Standard not found", Errors.InvalidInput, {
            sentryData: {
                contexts: {
                    extras: {
                        setId,
                        standardId,
                    },
                },
            },
        });
        return null;
    }

    /**
     * Given a standard in setId|standardId form, returns the relative URL to
     * the standard's mapping page. Throws an error if the standards data has
     * not yet been fetched from the server. Returns null if the setId or
     * standardId is not known.
     *
     * For example getStandardDescription("CCSS.Math|8.G.2.A") might return
     * "/standards/CCSS.Math/8.G#8.G.2.A"
     */
    getStandardRelativeUrl(standard: string): string | null {
        const [setId, standardId] = standard.split("|");
        const set = this.sets[setId];
        if (set) {
            const standard = set.standards[standardId];
            if (standard) {
                return standard.relativeUrl;
            }
        }
        // If we don't find the standard, then our standards data is out of sync
        // with our mappings data, and that is bad enough to log.
        Log.error("Standard not found", Errors.InvalidInput, {
            sentryData: {
                contexts: {
                    extras: {
                        setId,
                        standardId,
                    },
                },
            },
        });
        return null;
    }

    /**
     * Given a standard in setId|standardId form, returns the parent standard,
     * if there is one, in the same form. Throws an error if the standards data
     * has not yet been fetched from the server. Returns null if the setId
     * or the standardId is not known or if the stanadard has no parent.
     *
     * For example getParentStandard("CCSS.Math|8.G.2.A") might return
     * "CCSS.Math|8.G.2"
     */
    getParentStandard(standard: string): string | null | undefined {
        const [setId, standardId] = standard.split("|");
        const set = this.sets[setId];
        if (set) {
            const standard = set.standards[standardId];
            if (standard && standard.parent) {
                return `${setId}|${standard.parent}`;
            }
        }
        return null;
    }

    /**
     * Given a standard in setId|standardId form, this method returns
     * the standard in the object form expected by the standards-package
     * <Standards> component
     *
     * For example toObject("CCSS.Math|8.G.2.A") might return:
     *   {
     *       code: "8.2.G.A",
     *       descriptionHtml: "Understand the Pythagorean theorem",
     *       link: "/standards/CCSS.Math/8.G#8.G.2.A",
     *       set: {
     *           id: "CCSS.Math",
     *           isDiscoverable: false,
     *           prefix: "Common Core Math"
     *       }
     *   }
     */
    toObject(standard: string): Standard {
        const [setId, standardId] = standard.split("|");
        return {
            code: standardId,
            descriptionHtml: this.getStandardDescription(standard) || "",
            link: this.getStandardRelativeUrl(standard) || "",
            set: {
                id: setId,
                isDiscoverable: this.getSetIsDiscoverable(standard),
                prefix: this.getSetName(standard) || "",
            },
        };
    }

    /**
     * Given an iterable of standards in setId|standardId form, return an
     * array with elements sorted by set name and by order within the standard.
     * Note that sets of standards define the ordering for their standards.
     * In Common Core Math, for example, the "K" standards are defined to
     * come before the "HS" standards.
     */
    sorted(standards: Iterable<string>): Array<string> {
        standards = Array.from(standards); // copy and convert to an array
        // @ts-expect-error - TS2339 - Property 'sort' does not exist on type 'Iterable<string>'.
        standards.sort((a, b) => {
            const [setIdA, standardIdA] = a.split("|");
            const [setIdB, standardIdB] = b.split("|");
            const setA = this.sets[setIdA];
            const setB = this.sets[setIdB];

            if (!setA || !setB) {
                return 0;
            }

            if (setIdA !== setIdB) {
                return setA.name < setB.name ? -1 : 1;
            }
            const standardA = setA.standards[standardIdA];
            const standardB = setB.standards[standardIdB];
            if (!standardA || !standardB) {
                return 0;
            }
            return standardA.index - standardB.index;
        });
        // @ts-expect-error - TS2740 - Type 'Iterable<string>' is missing the following properties from type 'string[]': length, pop, push, concat, and 28 more.
        return standards;
    }
}
